/**
 * Free-form tag helpers (Track A · Phase-0 P0.6). Pure functions shared by
 * the tag widgets (`TagInput` / `TagFilter`), the register forms, the
 * Repository / Search filter rows, and the Insight Library facets.
 *
 * Tags are plain strings. Two are "equal" when they match
 * case-insensitively after trimming, but the **original casing of the
 * first occurrence is preserved** for display — so "AI" and "ai" collapse
 * to one chip rather than two. Namespaced tags (e.g. `opportunity:foo`,
 * see `taxonomy.ts`) are ordinary strings here; faceting by namespace is
 * the caller's concern.
 */

import { WORKSHOPS, type Workshop } from "@/content/workshops";

/** Normalise a tag for comparison/dedupe (trim + lowercase). */
export function normaliseTag(tag: string): string {
  return tag.trim().toLowerCase();
}

export type WorkshopChipAccent = {
  /** Token name used as `data-tag-accent` for CSS. */
  key: string;
  fg: string;
  rgb: string;
};

export type WorkshopChipStyle = {
  background: string;
  border: string;
  color: string;
  fontWeight?: number;
};

/** Same accent tokens the WorkshopNavigator step bar uses. */
const ACCENT_CSS: Record<Workshop["accent"], WorkshopChipAccent> = {
  "true-blue": {
    key: "true-blue",
    fg: "var(--accent-true-blue)",
    rgb: "var(--accent-true-blue-rgb)",
  },
  sky: {
    key: "sky",
    fg: "var(--accent-sky)",
    rgb: "var(--accent-sky-rgb)",
  },
  gold: {
    key: "gold",
    fg: "var(--accent-gold)",
    rgb: "var(--accent-gold-rgb)",
  },
  melon: {
    key: "melon",
    fg: "var(--accent-melon)",
    rgb: "var(--accent-melon-rgb)",
  },
  peach: {
    key: "peach",
    fg: "var(--accent-peach)",
    rgb: "var(--accent-peach-rgb)",
  },
};

/** Navigator-band accents keyed by workshop id (WS1/2 true-blue, …). */
const WORKSHOP_TAG_ACCENT: Record<string, WorkshopChipAccent> =
  Object.fromEntries(
    WORKSHOPS.map((w) => [w.id, ACCENT_CSS[w.accent]]),
  );

function chipStyleFromAccent(
  accent: WorkshopChipAccent,
  opts: { selected?: boolean } = {},
): WorkshopChipStyle {
  const selected = opts.selected ?? true;
  return {
    background: selected
      ? `rgba(${accent.rgb}, 0.18)`
      : `rgba(${accent.rgb}, 0.10)`,
    border: `1px solid rgba(${accent.rgb}, ${selected ? 0.55 : 0.32})`,
    color: accent.fg,
    fontWeight: selected ? 600 : 500,
  };
}

/** Workstream accent tokens for a workshop id (`WS1`…`WS8`). */
export function workshopAccent(
  workshopId: string,
): WorkshopChipAccent | undefined {
  const id = workshopId.trim().toUpperCase();
  return WORKSHOP_TAG_ACCENT[id];
}

/**
 * When a tag is exactly a workshop id (`WS1`…`WS8`), return chip colours
 * from that workshop's workstream accent. Other free-form tags return
 * `undefined` so callers keep their default chrome.
 */
export function workshopTagAccent(
  tag: string,
): WorkshopChipAccent | undefined {
  return workshopAccent(tag);
}

/** Chip styles for all tags inside a workshop modal / panel. */
export function workshopAccentChipStyle(
  workshopId: string,
  opts: { selected?: boolean } = {},
): WorkshopChipStyle | undefined {
  const accent = workshopAccent(workshopId);
  if (!accent) return undefined;
  return chipStyleFromAccent(accent, opts);
}

/** Chip styles for a workshop-id tag; `undefined` when not a WS id. */
export function workshopTagChipStyle(
  tag: string,
  opts: { selected?: boolean } = {},
): WorkshopChipStyle | undefined {
  const accent = workshopTagAccent(tag);
  if (!accent) return undefined;
  return chipStyleFromAccent(accent, opts);
}

/**
 * Resolve chip chrome for a tag:
 *   1. Literal `WS1`…`WS8` → that workshop's workstream accent
 *   2. Else ambient `workshopId` (modal / panel context) → that accent
 *   3. Else undefined (caller keeps its default true-blue chrome)
 */
export function resolveTagChipStyle(
  tag: string,
  workshopId?: string,
  opts: { selected?: boolean } = {},
): WorkshopChipStyle | undefined {
  return (
    workshopTagChipStyle(tag, opts) ??
    (workshopId ? workshopAccentChipStyle(workshopId, opts) : undefined)
  );
}

/** Accent key for `data-tag-accent` (CSS hook). */
export function resolveTagAccentKey(
  tag: string,
  workshopId?: string,
): string | undefined {
  return (
    workshopTagAccent(tag)?.key ??
    (workshopId ? workshopAccent(workshopId)?.key : undefined)
  );
}

/**
 * Collect the distinct tags across a set of records, preserving the casing
 * of each tag's first occurrence and returning them sorted
 * (case-insensitively). Records without tags contribute nothing. Blank /
 * whitespace-only tags are dropped.
 */
export function collectTags(
  records: ReadonlyArray<{ tags?: string[] }>,
): string[] {
  const seen = new Map<string, string>(); // normalised → first-seen display
  for (const rec of records) {
    for (const raw of rec.tags ?? []) {
      const norm = normaliseTag(raw);
      if (!norm) continue;
      if (!seen.has(norm)) seen.set(norm, raw.trim());
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  );
}

/**
 * Whether a record's tags satisfy the active filter.
 *   - No active tags → always matches (an empty filter narrows nothing).
 *   - `mode: "and"` (default) → the record must carry **every** active tag
 *     (each added filter narrows further — the Repository/Search behaviour).
 *   - `mode: "or"` → the record must carry **at least one** active tag.
 * Comparison is case-insensitive (via {@link normaliseTag}).
 */
export function matchesTags(
  tags: string[] | undefined,
  active: ReadonlyArray<string>,
  mode: "and" | "or" = "and",
): boolean {
  if (active.length === 0) return true;
  const have = new Set((tags ?? []).map(normaliseTag));
  const want = active.map(normaliseTag);
  return mode === "and"
    ? want.every((t) => have.has(t))
    : want.some((t) => have.has(t));
}

/**
 * Add a tag to a list, de-duplicating case-insensitively. Returns the same
 * array reference when the tag is blank or already present, so callers can
 * cheaply skip a no-op state update.
 */
export function addTag(tags: ReadonlyArray<string>, raw: string): string[] {
  const value = raw.trim();
  if (!value) return tags as string[];
  const norm = normaliseTag(value);
  if (tags.some((t) => normaliseTag(t) === norm)) return tags as string[];
  return [...tags, value];
}

/** Remove a tag (case-insensitive match). */
export function removeTag(tags: ReadonlyArray<string>, raw: string): string[] {
  const norm = normaliseTag(raw);
  return tags.filter((t) => normaliseTag(t) !== norm);
}
